// Copyright (c) 2025 WiseTime. All rights reserved.

import { Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import {
  DeleteTimesheetFieldOptionRequest,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { Empty } from "google-protobuf/google/protobuf/empty_pb"

type DeleteTimesheetFieldRpc = (request: DeleteTimesheetFieldOptionRequest) => Observable<Empty>

export const deleteTimesheetFieldOption = (deleteTimesheetFieldOptionRpc: DeleteTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteTimesheetFieldOption.match),
      map(action => action.payload),
      mergeMap(payload =>
        deleteTimesheetFieldOptionRpc(payload.request).pipe(
          mergeMap(() => {
            // Hide dialog
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            return of(Slice.deleteTimesheetFieldOption({
              timesheetFieldId: payload.timesheetFieldId,
              optionId: payload.request.getId(),
            }))
          }),
          catchError(e => handleError(e, payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.DeleteTimesheetFieldOptionPayload): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.FAILED_PRECONDITION) {
      // Hide dialog
      if (payload.onSuccess) {
        payload.onSuccess()
      }
      return of(showSnackBar({
        message: t({
          id: "msg_delete_timesheet_field_option_failed_precondition",
          message: "This option is in use and cannot be deleted. Please archive instead.",
        }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.NOT_FOUND) {
      // Hide dialog
      if (payload.onSuccess) {
        payload.onSuccess()
      }
      return of(Slice.deleteTimesheetFieldOption({
        timesheetFieldId: payload.timesheetFieldId,
        optionId: payload.request.getId(),
      }))
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      // Reset progress
      if (payload.onError) {
        payload.onError()
      }
      return of(showSnackBar({
        message: t({
          id: "msg_delete_timesheet_field_option_permission_denied",
          message: "Failed to delete timesheet field option. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  // Reset progress
  if (payload.onError) {
    payload.onError()
  }
  return of(showSnackBar({
    message: t({
      id: "msg_delete_timesheet_field_option_failed",
      message: "Failed to delete timesheet field option. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const deleteTimesheetFieldOptionRpc = (request: DeleteTimesheetFieldOptionRequest): Observable<Empty> =>
  grpc(() => timesheetFieldClient.deleteTimesheetFieldOption(request, null))

export default deleteTimesheetFieldOption(deleteTimesheetFieldOptionRpc)
